// /administracion/contratistas — Administración · Contratistas (handoff
// autogestión 2026-10-29 §8, plan 2026-10-30 §2.6, T-0qrc): la pantalla
// de facility sobre la nómina (/providers). routes/Contratistas.tsx sigue
// sirviendo a postventa sin cambios.
//
// Tabla Contratista · Especialidad · Abiertas · Estado · acciones, con
// «Editar» (drawer) y «Desactivar» (danger, inmediato: el handoff no pide
// confirmación y pausar no desasigna nada; el toast dice cuántas abiertas
// siguen asignadas). Un contratista pausado ofrece «Reactivar».
// «Agregar contratista» abre el mismo formulario.
//
// Lo que el handoff dibuja y aquí no aparece, porque providers no tiene
// esas columnas y esta ronda no migra (se oculta, no se simula): RUT
// (bajo el nombre) y «Dónde trabaja» (columna y campo). Bajo el nombre va
// la razón social, cuando difiere.
//
// Quién ve qué, como en Enlaces: la pantalla es de todo rol que LEE la
// nómina según la receta (canReadContratistas: `admin` o el carril
// `assigning_party`, el mismo gate de GET /providers), porque en facility
// el supervisor lee y amplía la nómina sin administrar la organización.
// Agregar sigue el gate de POST (ese mismo carril, no de solo lectura);
// Editar, Desactivar y Reactivar siguen el de PATCH (canAdministerOrg, el
// espejo de requireOrgAdmin) y a quien no administra no se le muestran.
// La guarda de ruta la monta App.tsx con canReadContratistas; aquí sólo
// se apaga la query para un rol sin lectura, así un montaje sin guarda
// nunca dispara un 403.

import { useState } from "react";
import { isReadOnlyRole, type ProviderListItem } from "@bitacorapro/api-types";
import { EmptyState } from "../../components/EmptyState";
import { Icon } from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { Panel } from "../../components/Panel";
import { QueryBoundary } from "../../components/QueryBoundary";
import { SkelTable } from "../../components/skeleton/SkelTable";
import {
  ContratistaDrawer,
  type ContratistaDrawerMode,
} from "../../features/administracion/contratistas/ContratistaDrawer";
import {
  canReadContratistas,
  desactivadoToast,
  estadoLabel,
  saveErrorMessage,
} from "../../features/administracion/contratistas/model";
import {
  useContratistas,
  usePatchContratista,
} from "../../features/administracion/contratistas/queries";
import { adminWords } from "../../lib/admin/words";
import { useRecipe } from "../../lib/api/queries";
import { useSession } from "../../lib/auth/useSession";
import { canAdministerOrg } from "../../lib/capabilities";
import { ADMIN_BASE_PATH } from "../../lib/nav";
import { ADMIN_TOAST_OPTS, useToast } from "../../lib/useToast";

export const CONTRATISTAS_PATH = `${ADMIN_BASE_PATH}/contratistas`;

// Cabeceras fijas para que el esqueleto pinte la misma fila que los datos.
const HEADERS = ["Contratista", "Especialidad", "Abiertas", "Estado", ""];

export default function Contratistas() {
  const session = useSession();
  const role = session?.organization.role;
  const recipeQuery = useRecipe();
  const recipe = recipeQuery.data;
  const readOnly = isReadOnlyRole(role ?? "");
  // Lee la nómina: el gate de GET /providers.
  const canRead = canReadContratistas(recipe, role);
  // Da de alta: el gate de POST /providers (mismo carril; los roles de
  // solo lectura, ADR-0164, reciben 403 ROLE_READ_ONLY).
  const canAdd = canRead && !readOnly;
  // Edita la ficha y el estado: el gate de PATCH /providers/:id, que lo
  // dice la receta (memberRoles[].orgAdmin) más el administrador de
  // plataforma, nunca el slug: es el espejo de requireOrgAdmin en core-api.
  const canManage =
    canAdministerOrg(recipe, role, { platformAdmin: session?.user.platformAdmin === true }) &&
    !readOnly;

  const words = adminWords(recipe);
  const findingWords = {
    finding: recipe?.labels.finding ?? "Observación",
    findingPlural: recipe?.labels.findingPlural ?? "Observaciones",
  };
  const toast = useToast();

  const list = useContratistas(canRead);
  const patch = usePatchContratista();
  const [open, setOpen] = useState<ContratistaDrawerMode | null>(null);
  // Ids con un PATCH de estado en vuelo. Un set y no `patch.variables`:
  // dos filas pulsadas seguidas son dos mutaciones vivas a la vez, y cada
  // una tiene que quedar deshabilitada hasta que vuelva la suya.
  const [enVuelo, setEnVuelo] = useState<ReadonlySet<string>>(() => new Set());

  function saved(message: string) {
    toast.show(message, "ok", ADMIN_TOAST_OPTS);
  }

  // mutateAsync y no mutate(vars, { onSuccess }): en TanStack Query v5 los
  // callbacks de `mutate` sólo corren para la última llamada consecutiva,
  // así que el toast de la primera fila se perdía si el gerente pulsaba
  // otra antes de que terminara. Con await, cada acción cierra su propio
  // toast (ok o error), igual que hace el drawer.
  async function cambiarEstado(p: ProviderListItem, status: "activo" | "pausado") {
    setEnVuelo((s) => new Set(s).add(p.id));
    try {
      const res = await patch.mutateAsync({ id: p.id, body: { status } });
      saved(
        status === "pausado"
          ? desactivadoToast(res.openAssigned, findingWords)
          : "Contratista reactivado"
      );
    } catch (err) {
      toast.show(saveErrorMessage(err), "err", ADMIN_TOAST_OPTS);
    } finally {
      setEnVuelo((s) => {
        const next = new Set(s);
        next.delete(p.id);
        return next;
      });
    }
  }

  // Receta cargada y sin lectura: App.tsx ya redirige; aquí no hay nada
  // que mostrar y la query quedó apagada.
  if (recipe !== undefined && !canRead) return null;

  return (
    <div className="page">
      <PageHeader
        eyebrow={words.module}
        title={words.sections.contratistas}
        sub="Nómina de contratistas a los que se asignan observaciones o mantenciones."
        actions={
          canAdd && (
            <button
              type="button"
              className="btn btn--primary"
              title="Abre el panel para agregar un contratista a la nómina"
              onClick={() => setOpen({ kind: "nuevo" })}
            >
              <Icon.Plus s={14} /> Agregar contratista
            </button>
          )
        }
      />

      <QueryBoundary
        query={list}
        waitingOn={[recipeQuery]}
        label="los contratistas"
        skeleton={
          <Panel flush>
            <SkelTable rows={6} cols={HEADERS.length} headers={HEADERS} />
          </Panel>
        }
        isEmpty={(data) => data.providers.length === 0}
        empty={
          <Panel flush>
            <EmptyState
              title="Sin contratistas"
              {...(canAdd && {
                description: `Agrega el primero con «Agregar contratista»: su nombre y una forma de contacto bastan para asignarle ${findingWords.findingPlural.toLowerCase()}.`,
              })}
            />
          </Panel>
        }
      >
        {(data) => (
          <Panel flush>
            <table className="dtable">
              <thead>
                <tr>
                  <th scope="col">Contratista</th>
                  <th scope="col">Especialidad</th>
                  <th scope="col" className="col-num">
                    Abiertas
                  </th>
                  <th scope="col">Estado</th>
                  {canManage && (
                    <th scope="col">
                      <span className="sr-only">Acciones</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.providers.map((p) => {
                  const activo = p.status === "activo";
                  const cambiando = enVuelo.has(p.id);
                  const legalDiffers = p.legalName !== null && p.legalName !== p.shortName;
                  return (
                    <tr key={p.id}>
                      <td>
                        <span className="cell-with-sub">
                          <span className="cell-strong">{p.shortName}</span>
                          {legalDiffers && <span className="cell-sub">{p.legalName}</span>}
                        </span>
                      </td>
                      <td>{p.specialty ? p.specialty : <span className="cell-meta">—</span>}</td>
                      <td className="col-num">
                        <span className="mono">{p.activeJobs}</span>
                      </td>
                      <td>
                        <span className={`st-pill st-pill--${activo ? "ok" : "idle"}`}>
                          <span className={`st-dot st-dot--${activo ? "ok" : "idle"}`} />
                          {estadoLabel(p.status)}
                        </span>
                      </td>
                      {canManage && (
                        <td className="cell-actions">
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            title="Cambiar nombre, razón social, especialidad o datos de contacto"
                            disabled={cambiando}
                            onClick={() => setOpen({ kind: "editar", item: p })}
                          >
                            Editar
                          </button>
                          {activo ? (
                            <button
                              type="button"
                              className="btn btn--danger btn--sm"
                              title="Pausa a este contratista; las observaciones ya asignadas no cambian"
                              disabled={cambiando}
                              onClick={() => void cambiarEstado(p, "pausado")}
                            >
                              Desactivar
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              title="Vuelve a activar a este contratista para asignarle observaciones"
                              disabled={cambiando}
                              onClick={() => void cambiarEstado(p, "activo")}
                            >
                              Reactivar
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Panel>
        )}
      </QueryBoundary>

      {open && <ContratistaDrawer mode={open} onClose={() => setOpen(null)} onSaved={saved} />}
    </div>
  );
}
